import { Injectable, PLATFORM_ID, DOCUMENT, DestroyRef, inject, NgZone } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { PreloadingStrategy, Route, Router, NavigationStart, NavigationEnd, NavigationCancel, NavigationError } from '@angular/router';
import { Observable, of, Subscription } from 'rxjs';

type Job = { route: Route; run: () => void; pending: boolean };

/** Only opted-in routes with pointer/keyboard intent. One speculative load at a time. */
@Injectable({ providedIn: 'root' })
export class IdlePreloadStrategy implements PreloadingStrategy {
  private platformId = inject(PLATFORM_ID);
  private ngZone = inject(NgZone);
  private document = inject(DOCUMENT);
  private router = inject(Router);
  private destroyRef = inject(DestroyRef);
  private jobs = new Set<Job>();
  private active = false;
  private idle: number | undefined;
  private navigating = false;
  private lastInput = 0;
  private timer: ReturnType<typeof setTimeout> | undefined;

  constructor() {
    if (!isPlatformBrowser(this.platformId)) return;
    this.ngZone.runOutsideAngular(() => {
      const intent = (event: Event) => {
        this.lastInput = Date.now();
        const anchor = (event.target as Element)?.closest?.('a[href]') as HTMLAnchorElement;
        if (!anchor || anchor.origin !== location.origin) return;
        for (const job of this.jobs) {
          const pattern = (job.route.path || '').split('/').filter(Boolean)
            .map(part => part.startsWith(':') ? '[^/]+' : part.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('/');
          if (pattern && new RegExp(`^/${pattern}/?$`).test(anchor.pathname)) job.pending = true;
        }
        this.schedule();
      };
      const input = () => { this.lastInput = Date.now(); };
      this.document.addEventListener('pointerover', intent, { passive: true });
      this.document.addEventListener('focusin', intent);
      this.document.addEventListener('pointerdown', input, { passive: true });
      this.document.addEventListener('keydown', input, { passive: true });
      const events = this.router.events.subscribe(event => {
        if (event instanceof NavigationStart) this.navigating = true;
        if (event instanceof NavigationEnd || event instanceof NavigationCancel || event instanceof NavigationError) {
          this.navigating = false;
          this.schedule();
        }
      });
      this.destroyRef.onDestroy(() => {
        clearTimeout(this.timer);
        if (this.idle !== undefined) (window as any).cancelIdleCallback?.(this.idle);
        events.unsubscribe();
        this.jobs.clear();
        this.document.removeEventListener('pointerover', intent);
        this.document.removeEventListener('focusin', intent);
        this.document.removeEventListener('pointerdown', input);
        this.document.removeEventListener('keydown', input);
      });
    });
  }

  private allowed(): boolean {
    const connection = (navigator as any).connection;
    return !connection?.saveData && !/^(slow-2g|2g|3g)$/.test(connection?.effectiveType || '')
      && !(connection?.downlink > 0 && connection.downlink < 1.5);
  }

  preload(route: Route, load: () => Observable<any>): Observable<any> {
    if (!isPlatformBrowser(this.platformId) || route.data?.['preload'] !== 'intent') return of(null);
    return new Observable(observer => {
      let subscription: Subscription | undefined;
      let running = false;
      const job: Job = { route, pending: false, run: () => {
        this.active = running = true;
        this.jobs.delete(job);
        const finish = () => { if (!running) return; running = false; this.active = false; observer.complete(); this.schedule(); };
        try {
          subscription = load().subscribe({ next: value => observer.next(value), error: finish, complete: finish });
        } catch { finish(); }
      } };
      this.jobs.add(job);
      return () => {
        this.jobs.delete(job); subscription?.unsubscribe();
        if (running) { running = false; this.active = false; this.schedule(); }
      };
    });
  }

  private schedule(): void {
    if (this.timer || this.idle !== undefined || this.active || ![...this.jobs].some(job => job.pending)) return;
    this.ngZone.runOutsideAngular(() => {
      this.timer = setTimeout(() => {
        this.timer = undefined;
        if (!this.allowed()) { for (const job of this.jobs) job.pending = false; return; }
        if (this.navigating || Date.now() - this.lastInput < 500) { this.schedule(); return; }
        const run = () => {
          this.idle = undefined;
          if (this.navigating || Date.now() - this.lastInput < 500 || !this.allowed()) { this.schedule(); return; }
          const job = [...this.jobs].find(item => item.pending);
          job?.run();
        };
        const win = window as any;
        if (win.requestIdleCallback) this.idle = win.requestIdleCallback(run, { timeout: 1500 });
        else run();
      }, 700);
    });
  }
}
